import {
	AkamaiLocationCookieModel,
	GBPEProfileAddress,
	GMXLocationChangeEventDetail,
	GMXLocationDetail,
	GMXLocationSource,
	GMXLocationValue,
	LOCATION_CHANGE_EVENT,
	LOCATION_READY_EVENT,
} from '../../../models/location.model'
import { deleteCookie, getCookieValByName, getTopLevelCookieDomain, setCookie } from '../../../utils/cookies'
import { GMXGeoIPCountryConfirmationPanel } from '../panel/geoip-country-confirmation-panel'
import { GeoIPPanelDataModel } from '../../../models/geoip-panel-data.model'
import DOMPurify from 'dompurify'

/**
 * Location Singleton
 *
 * Single source of truth for the visitor's zip/postal code across every app on a
 * brand site.  Resolves a value by precedence — an explicitly entered zip beats the
 * signed-in profile, which beats the Akamai geo-IP guess — and persists the result
 * as one JSON cookie so the value survives navigation between apps.
 *
 * Changes are broadcast as a `gmx-location-change` CustomEvent on `window`.  There is
 * no registration step: an app listens if it wants to react, and reads `get()` or
 * `resolve()` if it only needs the current value.
 *
 * Whatever it is handed is what it stores.  Every caller is a first-party app, so a zip
 * is not format-checked here; an app that needs to know whether a visitor typed
 * something sensible should check before it calls.
 */

// The event names live in the model so consumers can listen without importing this module.
export { LOCATION_CHANGE_EVENT, LOCATION_READY_EVENT }

export const COOKIE_NAME = 'gm_location'
export const TTL_DAYS = 30

/** US ZIP+4, as GBPE profile addresses commonly store it. */
const ZIP_PLUS_FOUR_PATTERN = /^(\d{5})-\d{4}$/

/** Cookie written at the CDN edge; the lowest-confidence source. */
const AKAMAI_COOKIE_NAME = 'GMWP_location'
const MS_PER_DAY = 24 * 60 * 60 * 1000

const SOURCE_PRECEDENCE: Record<GMXLocationSource, number> = {
	akamai: 1,
	profile: 2,
	user_input: 3,
}

/**
 * The edge writes US zips as a range list (`77001-77096+77098-77099`), of which the first
 * entry is the effective one.  Only numeric values are split — a Canadian code carries its
 * own separator (`A1A 1A1`) and splitting it would leave the useless leading half.
 */
function firstAkamaiZip(value: string | undefined): string | undefined {
	if (!value) {
		return undefined
	}

	return /^\d/.test(value) ? value.split(/[-+ ]/g)[0] : value
}

// Worked out once per page: deriving it probes the browser with throwaway cookie writes.
let cookieDomain: string | undefined

function getCookieDomain(): string {
	if (cookieDomain === undefined) {
		cookieDomain = getTopLevelCookieDomain()
	}

	return cookieDomain
}

// The exported instance is frozen, so every piece of mutable state lives out here.
const resolveInFlightMap = new WeakMap<GMXLocationSingleton, Promise<GMXLocationValue | null>>()
const clearCountMap = new WeakMap<GMXLocationSingleton, number>()

export class GMXLocationSingleton {
	constructor() {
		if (!window.gmxLocationSingleton) {
			window.gmxLocationSingleton = this

			// GBPE loads well after this singleton, so the profile tier is re-checked
			// whenever auth settles rather than only on first resolve.
			window.addEventListener('gbpe:authStateUpdated', () => {
				this._dropProfileLocationOnSignOut()
				void this.resolve(true)
			})

			window.dispatchEvent(new CustomEvent(LOCATION_READY_EVENT))

			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this._geoIPDetection(), { once: true })
			} else {
				this._geoIPDetection()
			}
		} else {
			window.console.log('Tried to instantiate location singleton...already instantiated')
		}
	}

	private get _clearCount(): number {
		return clearCountMap.get(this) || 0
	}

	/** Current effective location, or null when nothing is stored or the value has aged out. */
	public get(): GMXLocationValue | null {
		const raw = getCookieValByName(COOKIE_NAME)

		if (!raw) {
			return null
		}

		let payload: GMXLocationValue

		try {
			payload = JSON.parse(decodeURIComponent(raw))
		} catch {
			return null
		}

		if (!payload?.zip || this._isExpired(payload.timestamp)) {
			return null
		}

		return payload
	}

	public getZip(): string | null {
		return this.get()?.zip || null
	}

	/** Write a zip the visitor entered.  Always wins over profile and Akamai values. */
	public setUserZip(zip: string, detail: GMXLocationDetail = {}): GMXLocationValue {
		return this.set(zip, 'user_input', detail)
	}

	/**
	 * Write a zip from any source, subject to the precedence gate.  Returns the value in
	 * effect afterwards, which is the unchanged existing one when a lower-precedence
	 * write is turned away.
	 */
	public set(zip: string, source: GMXLocationSource, detail: GMXLocationDetail = {}): GMXLocationValue {
		const previous = this.get()

		if (previous && SOURCE_PRECEDENCE[source] < SOURCE_PRECEDENCE[previous.source]) {
			return previous
		}

		const next: GMXLocationValue = {
			zip: this._normalizeZip(zip),
			source,
			timestamp: new Date().toISOString(),
		}

		if (detail.country) {
			next.country = detail.country
		}
		if (detail.region) {
			next.region = detail.region
		}
		if (detail.city) {
			next.city = detail.city
		}
		if (detail.county) {
			next.county = detail.county
		}

		this._persist(next)

		// A same-source, same-zip write only renews the TTL, so it is not a change.
		if (!previous || previous.zip !== next.zip || previous.source !== next.source) {
			this._emitChange(next, previous)
		}

		return next
	}

	/** Expire the stored location and announce the change. */
	public clear(): void {
		const previous = this.get()

		deleteCookie(COOKIE_NAME, { domain: getCookieDomain() })
		resolveInFlightMap.delete(this)
		clearCountMap.set(this, this._clearCount + 1)

		if (previous) {
			this._emitChange(null, previous)
		}
	}

	/**
	 * Run the full precedence chain, filling in a profile or Akamai value when no
	 * higher-confidence zip is stored.  Concurrent callers share one resolution.
	 */
	public resolve(forceRefresh = false): Promise<GMXLocationValue | null> {
		const inFlight = resolveInFlightMap.get(this)

		if (inFlight && !forceRefresh) {
			return inFlight
		}

		const pending = this._resolve().finally(() => {
			if (resolveInFlightMap.get(this) === pending) {
				resolveInFlightMap.delete(this)
			}
		})

		resolveInFlightMap.set(this, pending)

		return pending
	}

	private async _resolve(): Promise<GMXLocationValue | null> {
		const current = this.get()

		if (current?.source === 'user_input') {
			return current
		}

		const clearCount = this._clearCount
		const profileZip = await this._readProfileZip()

		// A clear() while the profile was loading means the visitor's location was
		// deliberately dropped, so finishing the chain here would resurrect it.
		if (clearCount !== this._clearCount) {
			return this.get()
		}

		if (profileZip) {
			return this.set(profileZip, 'profile')
		}

		if (!current) {
			const akamaiInfo = this._readAkamaiCookie()

			if (akamaiInfo) {
				return this.set(akamaiInfo.zip, 'akamai', {
					country: akamaiInfo.country_code,
					region: akamaiInfo.region_code,
					city: akamaiInfo.city,
					county: akamaiInfo.county,
				})
			}
		}

		return this.get()
	}

	/**
	 * A profile zip would otherwise outlive the session it came from and keep reporting
	 * `profile` to analytics with nobody signed in.  Dropping it lets the chain re-seed
	 * from geo-IP.  Only that tier goes — a zip the visitor typed is their own and stays.
	 */
	private _dropProfileLocationOnSignOut(): void {
		if (window.gbpe?.auth?.currentAuthState === 1) {
			return
		}

		if (this.get()?.source === 'profile') {
			this.clear()
		}
	}

	private async _readProfileZip(): Promise<string | null> {
		const gbpe = window.gbpe

		if (!gbpe?.auth || gbpe.auth.currentAuthState !== 1 || !gbpe.user) {
			return null
		}

		try {
			const profile = await gbpe.user.profile
			const addresses: GBPEProfileAddress[] = profile?.data?.addresses

			if (!Array.isArray(addresses)) {
				return null
			}

			// An account can hold several addresses.  Honour whichever one the user marked
			// preferred; the flag is often false across the board, so fall back to the first.
			const address = addresses.find(entry => entry?.preferred_address_flag) || addresses[0]
			const zip = address?.postal_code

			return typeof zip === 'string' && zip ? zip : null
		} catch (error) {
			window.console.warn(`Failed to read postal code from GBPE profile: ${error}`)

			return null
		}
	}

	/**
	 * Parse the CDN-set location cookie.  Returns null unless it carries a zip, since
	 * the surrounding geo detail is only useful alongside one.
	 */
	private _readAkamaiCookie(): AkamaiLocationCookieModel | null {
		const raw = document.cookie.split('; ').find(row => row.startsWith(`${AKAMAI_COOKIE_NAME}=`))

		if (!raw) {
			return null
		}

		const info: AkamaiLocationCookieModel = {}

		raw.slice(AKAMAI_COOKIE_NAME.length + 1)
			.split(',')
			.forEach(pair => {
				const splitPair = pair.split('=')

				switch (splitPair[0]) {
					case 'country_code':
						info.country_code = splitPair[1]
						break
					case 'region_code':
						info.region_code = splitPair[1]
						break
					case 'city':
						info.city = splitPair[1]
						break
					case 'county':
						info.county = splitPair[1]
						break
					case 'zip':
						info.zip = firstAkamaiZip(splitPair[1])
						break
					default:
						break
				}
			})

		return info.zip ? info : null
	}

	/**
	 * Collapse a US ZIP+4 to its 5-digit base, which is what every consumer expects.
	 * Canadian codes are untouched — their separator is part of the code itself.
	 */
	private _normalizeZip(zip: string): string {
		return zip.trim().replace(ZIP_PLUS_FOUR_PATTERN, '$1')
	}

	private _isExpired(timestamp: string): boolean {
		const writtenAt = Date.parse(timestamp)

		if (Number.isNaN(writtenAt)) {
			return true
		}

		return Date.now() - writtenAt > TTL_DAYS * MS_PER_DAY
	}

	private _persist(value: GMXLocationValue): void {
		setCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(value)), {
			maxAgeSeconds: TTL_DAYS * 24 * 60 * 60,
			domain: getCookieDomain(),
		})
	}

	private _emitChange(location: GMXLocationValue | null, previous: GMXLocationValue | null): void {
		window.dispatchEvent(
			new CustomEvent<GMXLocationChangeEventDetail>(LOCATION_CHANGE_EVENT, {
				detail: { location, previous },
			})
		)
	}

	private _geoIPDetection(): void {
		try {
			// test akamai cookie value: country_code=US,region_code=TX,city=CEDARPARK,county=WILLIAMSON,zip=78613+78630
			const akamaiLocationCookie = this._readAkamaiCookie()
			const pageCountry =
				(document.documentElement.lang || '')
					.split('-')
					.slice(1)
					.find(subtag => /^[A-Za-z]{2}$/.test(subtag) || /^[0-9]{3}$/.test(subtag))
					?.toUpperCase() || ''

			if (
				!GMXLocationSingleton.getGeoIpSession()?.hideToast &&
				akamaiLocationCookie?.country_code &&
				akamaiLocationCookie.country_code !== pageCountry
			) {
				const geoIPScript = document.getElementById('gmx-geoip-country-confirmation-panel-data')

				if (geoIPScript) {
					let geoIPData: GeoIPPanelDataModel

					try {
						geoIPData = JSON.parse(geoIPScript.textContent || '{}') as GeoIPPanelDataModel
					} catch (error) {
						window.console.error(`GeoIP:  Failed to parse geo-IP panel data: ${error}`)
						return
					}

					const defaultDropdownCountryCode =
						geoIPData.redirectDropdownOptions?.find(
							option => option.countryCode === akamaiLocationCookie.country_code
						)?.countryCode || pageCountry

					const newPanelEl = document.createElement(
						'gmx-geoip-country-confirmation-panel'
					) as GMXGeoIPCountryConfirmationPanel
					newPanelEl.setAttribute('data-panel-type', 'full')
					newPanelEl.setAttribute('data-panel-loaded', 'true')
					newPanelEl.setAttribute('data-show', '')

					newPanelEl.innerHTML = DOMPurify.sanitize(
						`
						<div class="panel-container">
							<div class="panel-content">
								<div class="panel-header">
									<div class="cta-container">
										<button
											class="close-btn gmx-btn gmx-btn-secondary icon-only"
											role="button"
											aria-label="${geoIPData.panelCloseBtnAriaLabel}"
											data-icon-name="close"
										></button>
									</div>
								</div>
								<div class="panel-content-body">
									<gmx-layout class="gmx-layout-sm-col1 gmx-inset-md-col2 gmx-inset-lg-col-reset gmx-add-row-gap">
										<div class="title">
											${geoIPData.title}
										</div>
										<div class="subtitle">
											${geoIPData.subtitle}
										</div>
										<div>
											<div data-icon-name="chevron" data-icon-dir="down" class="gmx-form-field-container gmx-dropdown">
												<select id="gmx-geoip-country-dropdown" class="">
													${geoIPData.redirectDropdownOptions
														.map(
															option =>
																`<option data-site-url="${option.siteUrl}" value="${option.countryCode}" ${defaultDropdownCountryCode === option.countryCode ? 'selected' : ''}>${option.countryName}</option>`
														)
														.join('')}
												</select>
												<label for="gmx-geoip-country-dropdown">${geoIPData.redirectDropdownLabel}</label>
											</div>
										</div>
									</gmx-layout>
								</div>
								<div class="panel-footer">
									<gmx-layout class="gmx-sm-grid-padding gmx-add-row-gap gmx-inset-md-col2 gmx-inset-lg-col-reset">
										<div>
											<button class="gmx-btn gmx-btn-primary size-lg" data-geoip-confirm-btn>
												${geoIPData.confirmCTALabel}
											</button>
										</div>
									</gmx-layout>
								</div>
							</div>
						</div>
					`,
						{
							ALLOWED_TAGS: ['div', 'button', 'gmx-layout', 'a', 'span', 'select', 'option', 'label'],
							ADD_ATTR: ['selected'],
						}
					)

					const countryDropdown = newPanelEl.querySelector(
						'#gmx-geoip-country-dropdown'
					) as HTMLSelectElement | null

					if (countryDropdown) {
						countryDropdown.value = defaultDropdownCountryCode
					}

					document.body.appendChild(newPanelEl)
				}
			}
		} catch (error) {
			window.console.error(`Failed to parse geo-IP panel data: ${error}`)
		}
	}

	public static getGeoIpSession(): Record<string, string> {
		let sessionData: Record<string, string> = {}

		try {
			sessionData = sessionStorage.getItem('gb-geo-ip-notification')
				? JSON.parse(sessionStorage.getItem('gb-geo-ip-notification'))
				: {}
		} catch {
			window.console.error('Failed to parse geo-IP session data from sessionStorage')
		}

		return sessionData
	}
	public static setGeoIpSession(hideToast: boolean): void {
		sessionStorage.setItem('gb-geo-ip-notification', JSON.stringify({ hideToast: hideToast }))
	}

	public getAkamaiLocationCookieInfo(): AkamaiLocationCookieModel | null {
		return this._readAkamaiCookie()
	}
}

const locationSingleton = Object.freeze(new GMXLocationSingleton())
export default locationSingleton
